import type {
  FlightFilterChip,
  FlightFilterIntent,
  FlightFilterPatch,
} from './filter_intent_source.js';
import type { FlightOfferSource } from './flight_offer_source.js';
import {
  DAY_MINUTES_END,
  type DayMinuteRange,
  type FlightFilterState,
  type StopsChoice,
} from './filter_logic.js';

/**
 * Os CHIPS do filtro por texto (spec flight-text-filter-chips D2 — híbrido): cada chip carrega o
 * patch que aplicou e o `undo` (os valores que sobrescreveu). Patch em eixo do painel MOVE o
 * controle — nada de corte escondido; eixo sem controle (conexão) vive só como chip. Puro: o
 * reducer do explorer delega aqui.
 */
type FilterChipStatus = 'pending' | 'applied' | 'unsupported' | 'unresolved';

export interface FilterChip {
  id: string;
  /** O texto cru (rótulo enquanto pendente; telemetria depois). */
  text: string;
  status: FilterChipStatus;
  axis: FlightFilterChip['axis'];
  patch?: FlightFilterPatch;
  /** Os valores que o patch SOBRESCREVEU — o ✕ os devolve. Só em `applied`. */
  undo?: Partial<FlightFilterState>;
  reasonCode?: FlightFilterChip['reasonCode'];
  confidence?: number;
  /** Perna que a frase nomeou (índice do roteiro). Ausente = perna ativa na hora do pedido. */
  leg?: number;
  /** Chip de OUTRA perna, guardado até ela abrir (R2-D3): tem `patch`, não tem `undo`, não tocou o filtro. */
  deferred?: boolean;
}

export interface ChipsContext {
  /** Companhias conhecidas (facetas ou lista em mão): "sem Gol" vira o complemento. */
  airlinesKnown: string[];
  /** Aeroportos de PONTA conhecidos (lista em mão): "sem Congonhas" idem. */
  airportsKnown: string[];
}

export interface ChipsSlice {
  filter: FlightFilterState;
  chips: FilterChip[];
}

const clockToMinute = (hhmm: string): number =>
  Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const windowToRange = (window: { start?: string; end?: string }): DayMinuteRange => [
  window.start ? clockToMinute(window.start) : 0,
  window.end ? clockToMinute(window.end) : DAY_MINUTES_END,
];
const STOPS_OF: Record<NonNullable<FlightFilterPatch['stops']>, StopsChoice[]> = {
  nonstop: ['nonstop'],
  up_to_1: ['up_to_1'],
  up_to_2: ['up_to_2'],
  any: [],
};

/** Patch do fio → campos do estado do painel. Campo que o estado não conhece é ignorado (fio loose). */
function patchToState(patch: FlightFilterPatch, ctx: ChipsContext): Partial<FlightFilterState> {
  const out: Partial<FlightFilterState> = {};
  const { airlines, airports } = patch;
  if (airlines)
    out.airlines = patch.airlinesExclude
      ? ctx.airlinesKnown.filter((c) => !airlines.includes(c))
      : airlines;
  if (patch.stops) out.stops = STOPS_OF[patch.stops];
  if (patch.departureWindow) out.departureRange = windowToRange(patch.departureWindow);
  if (patch.arrivalWindow) out.arrivalRange = windowToRange(patch.arrivalWindow);
  if (patch.minPrice !== undefined) out.minPrice = patch.minPrice;
  if (patch.maxPrice !== undefined) out.maxPrice = patch.maxPrice;
  if (patch.withinPolicyOnly !== undefined) out.withinPolicyOnly = patch.withinPolicyOnly;
  if (patch.avoidAirports) out.avoidAirports = patch.avoidAirports;
  if (patch.viaAirports) out.viaAirports = patch.viaAirports;
  if (airports)
    out.airports = patch.airportsExclude
      ? ctx.airportsKnown.filter((a) => !airports.includes(a))
      : airports;
  if (patch.sort) out.sort = patch.sort;
  return out;
}

const touches = (chip: FilterChip, keys: (keyof FlightFilterState)[]): boolean =>
  chip.undo !== undefined && keys.some((key) => Object.hasOwn(chip.undo ?? {}, key));

export function pendingChip(slice: ChipsSlice, id: string, text: string): ChipsSlice {
  return {
    filter: slice.filter,
    chips: [...slice.chips, { id, text, status: 'pending', axis: 'other' }],
  };
}

/** O corpo por chip do fio dentro de `resolveChips` (extraído para manter a complexidade cognitiva
 *  do laço sob o teto): decide entre chip sem patch, patch sem eixo conhecido (`unresolved`) e
 *  patch aplicado (move o filtro, droppa quem tocava os mesmos campos, guarda o undo). */
function applyWireChip(
  chips: FilterChip[],
  filter: FlightFilterState,
  base: FilterChip,
  wire: FlightFilterChip,
  ctx: ChipsContext,
): { chips: FilterChip[]; filter: FlightFilterState } {
  if (wire.status !== 'applied' || !wire.patch) {
    return { chips: [...chips, base], filter };
  }
  const next = patchToState(wire.patch, ctx);
  const keys = Object.keys(next) as (keyof FlightFilterState)[];
  if (keys.length === 0) {
    return { chips: [...chips, { ...base, status: 'unresolved', reasonCode: 'other' }], filter };
  }
  const kept = chips.filter((chip) => !touches(chip, keys));
  const undo = Object.fromEntries(
    keys.map((key) => [key, filter[key]]),
  ) as Partial<FlightFilterState>;
  return { chips: [...kept, { ...base, patch: wire.patch, undo }], filter: { ...filter, ...next } };
}

/** Onde o chip com `leg` cai (R2-D3): a perna em cena e o tamanho do roteiro. `activeEmpty` = a
 *  perna em cena ainda não tem lista (ver `resolveLeggedChip`); ausente conta como "tem". */
interface ChipLegs {
  active: number;
  count: number;
  activeEmpty?: boolean;
}

/** O destino de um chip de OUTRA perna dentro de `resolveChips` (extraído para manter a
 *  complexidade cognitiva do laço sob o teto): fora do roteiro vira `unresolved`; `applied`
 *  que não é a perna ativa fica `deferred` (patch guardado, sem tocar o filtro); os demais
 *  seguem para o caminho normal (`undefined` = "não decidiu"). Muta `base.leg` mesmo quando
 *  devolve `undefined` — o caminho normal também guarda de qual perna o chip veio.
 *
 *  A perna ATIVA ainda sem lista (`activeEmpty`) também espera `deferred` (#3559): "na volta, só
 *  direto" pedido na ida + a ida escolhida antes da resposta abre a volta `loading`, e o juiz
 *  responde `leg` = a perna já ativa. Aplicar ali cunhava o corte sobre `[]` — e o primeiro quadro
 *  não-vazio da perna (`search_ok`/`refine_ok` firstFrame) passa por `resetFilter`, que zera o
 *  filtro e só carrega deferred/pendente (`carryDeferred`): o chip aplicado sumia junto do corte.
 *  Deferred, quem aplica é o `applyDeferred` desse mesmo quadro — a régua do chip de outra perna. */
function resolveLeggedChip(
  chips: FilterChip[],
  base: FilterChip,
  wire: FlightFilterChip,
  legs: ChipLegs,
): FilterChip[] | undefined {
  if (wire.leg === undefined) return undefined;
  if (wire.leg >= legs.count) {
    return [...chips, { ...base, status: 'unresolved', reasonCode: 'other' }];
  }
  base.leg = wire.leg;
  const waits = wire.leg !== legs.active || legs.activeEmpty === true;
  if (waits && wire.status === 'applied' && wire.patch) {
    return [...chips, { ...base, patch: wire.patch, deferred: true }];
  }
  return undefined;
}

/** O intent pousou: o pendente vira 1..N chips (um por eixo). `applied` entra no filtro com undo;
 *  chip anterior que tocava os MESMOS campos sai (o pedido novo é a verdade). Kind ≠ filter ⇒ só o
 *  pendente some (a nota é do componente). */
export function resolveChips(
  slice: ChipsSlice,
  id: string,
  intent: FlightFilterIntent,
  ctx: ChipsContext,
  legs?: ChipLegs,
): ChipsSlice {
  const pending = slice.chips.find((chip) => chip.id === id);
  if (!pending) return slice;
  let filter = slice.filter;
  let chips = slice.chips.filter((chip) => chip.id !== id);
  if (intent.kind !== 'filter') return { filter, chips };
  for (const [index, wire] of intent.chips.entries()) {
    const base: FilterChip = {
      id: `${id}:${String(index)}`,
      text: pending.text,
      status: wire.status,
      axis: wire.axis,
      ...(wire.reasonCode ? { reasonCode: wire.reasonCode } : {}),
      ...(wire.confidence !== undefined ? { confidence: wire.confidence } : {}),
    };
    const legged = legs ? resolveLeggedChip(chips, base, wire, legs) : undefined;
    if (legged) {
      chips = legged;
      continue;
    }
    ({ chips, filter } = applyWireChip(chips, filter, base, wire, ctx));
  }
  return { filter, chips };
}

/**
 * O que sobrevive a um reset de filtro: os chips guardados para outra perna (`deferred`) E o
 * PENDENTE.
 *
 * O pendente é um pedido EM VOO — o juiz ainda não respondeu (1–3 s). Abrir uma perna
 * (`go_to_leg`, `fares_picked`, `pick_removed`, o primeiro quadro de `search_ok`/`refine_ok`:
 * todos passam por `resetFilter`) não é resposta a ele, e derrubá-lo aqui matava o pedido em
 * silêncio: "na volta, só direto" digitado na ida + a escolha da ida antes da resposta, e o
 * `chip_failed` que chegava depois não achava mais o chip pra marcar nada (#3559). Quem descarta
 * um pendente é só a RESPOSTA — `chip_failed` ou `chip_resolved`, e é o hook quem decide qual.
 *
 * `applyDeferred` o ignora de propósito: ele exige `deferred === true` e `patch`, e o pendente
 * não tem nenhum dos dois.
 */
export function carryDeferred(chips: FilterChip[]): FilterChip[] {
  return chips.filter((chip) => chip.deferred === true || chip.status === 'pending');
}

/** A perna abriu: os deferred dela entram no filtro (com undo) como se tivessem acabado de pousar. */
export function applyDeferred(slice: ChipsSlice, leg: number, ctx: ChipsContext): ChipsSlice {
  let filter = slice.filter;
  let chips: FilterChip[] = [];
  for (const chip of slice.chips) {
    if (chip.deferred !== true || chip.leg !== leg || !chip.patch) {
      chips.push(chip);
      continue;
    }
    const live = { ...chip };
    delete live.deferred;
    ({ chips, filter } = applyWireChip(
      chips,
      filter,
      live,
      { axis: chip.axis, status: 'applied', patch: chip.patch },
      ctx,
    ));
  }
  return { filter, chips };
}

/** Companhias/aeroportos conhecidos para "sem X" virar complemento: facetas quando há, senão a lista em mão. */
export function chipsContextOf(
  offers: readonly FlightOfferSource[],
  facets?: { airlines: readonly { code: string }[] },
): ChipsContext {
  const unique = <T>(items: (T | undefined)[]): T[] => [
    ...new Set(items.filter((item): item is T => item !== undefined)),
  ];
  const segments = offers.flatMap((offer) => [
    ...offer.segments,
    ...(offer.returnLeg?.segments ?? []),
  ]);
  return {
    airlinesKnown: facets
      ? facets.airlines.map((airline) => airline.code)
      : unique(segments.map((segment) => segment.carrier)),
    airportsKnown: unique(
      offers.flatMap((offer) => [offer.segments[0]?.from, offer.segments.at(-1)?.to]),
    ),
  };
}

export function removeChip(slice: ChipsSlice, id: string): ChipsSlice {
  const chip = slice.chips.find((candidate) => candidate.id === id);
  if (!chip) return slice;
  return {
    filter: chip.undo ? { ...slice.filter, ...chip.undo } : slice.filter,
    chips: slice.chips.filter((candidate) => candidate.id !== id),
  };
}

export function failChip(slice: ChipsSlice, id: string): ChipsSlice {
  return { filter: slice.filter, chips: slice.chips.filter((chip) => chip.id !== id) };
}

/** Mexida MANUAL no painel: chips que tocaram um campo alterado caem, sem undo (o gesto é a verdade). */
export function dropChipsTouching(
  chips: FilterChip[],
  prev: FlightFilterState,
  next: FlightFilterState,
): FilterChip[] {
  const changed = (Object.keys(next) as (keyof FlightFilterState)[]).filter(
    (key) => JSON.stringify(prev[key]) !== JSON.stringify(next[key]),
  );
  if (changed.length === 0) return chips;
  return chips.filter((chip) => !touches(chip, changed));
}
